import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('authToken'));

  useEffect(() => {
    // Check for token in URL hash (from admin dashboard)
    const hash = window.location.hash;
    if (hash.includes('token=')) {
      const urlToken = hash.split('token=')[1].split('&')[0];
      if (urlToken && urlToken !== token) {
        localStorage.setItem('authToken', urlToken);
        setToken(urlToken);
        // Clean up URL
        window.location.hash = '';
        return;
      }
    }

    if (token) {
      // Set the authorization header for API requests
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Fetch current user info
      getCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const getCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to get current user:', error);
      // Token might be expired, clear it
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await api.post('/auth/login', {
        username,
        password
      });
      
      const { access_token } = response.data;
      
      // Store token
      localStorage.setItem('authToken', access_token);
      setToken(access_token);
      
      // Set authorization header
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Fetch user info
      await getCurrentUser();
      
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setToken(null);
    setUser(null);
    delete api.defaults.headers.common['Authorization'];
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    
    // Define role permissions (Healthcare Domain)
    const rolePermissions = {
      admin: [
        'classify_documents',
        'manage_compliance',
        'manage_protocols',
        'safety_monitoring',
        'manage_configs',
        'manage_users',
        'upload_documents',
        'view_documents'
      ],
      compliance_officer: [
        'classify_documents',
        'manage_compliance',
        'manage_protocols',
        'safety_monitoring',
        'upload_documents',
        'view_documents'
      ],
      clinical_researcher: [
        'classify_documents',
        'manage_protocols',
        'safety_monitoring',
        'upload_documents',
        'view_documents'
      ],
      data_analyst: [
        'classify_documents',
        'safety_monitoring',
        'upload_documents',
        'view_documents'
      ],
      reviewer: [
        'classify_documents',
        'upload_documents',
        'view_documents'
      ]
    };

    const userPermissions = rolePermissions[user.role] || [];
    return userPermissions.includes(permission);
  };

  const hasRole = (role) => {
    return user?.role === role;
  };

  const isAuthenticated = () => {
    return !!user && !!token;
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    hasPermission,
    hasRole,
    isAuthenticated,
    getCurrentUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};